import { Controller } from "@hotwired/stimulus"

/**
 * Lightweight controller for overlay editing forms (stroke, fill, effects, layout).
 *
 * Dispatches structured window CustomEvents from form inputs.
 * Replaces inline oninput/onchange/onclick handlers with Stimulus data-actions.
 *
 * Usage:
 *   <div data-controller="overlay-form">
 *     <input type="range" data-action="input->overlay-form#send"
 *            data-overlay-form-event-param="shape:set-stroke-width" />
 *   </div>
 */
export default class extends Controller {
  static targets = ["shadowControls"]

  /** Generic event dispatch — sliders, number inputs, buttons.
   *  Reads the event name from data-overlay-form-event-param.
   *  Builds detail from the input value automatically.
   *  Auto-updates sibling <output> for range inputs.
   */
  send(e) {
    const eventName = e.params.event
    if (!eventName) return

    const detail = this._buildDetail(e)
    window.dispatchEvent(new CustomEvent(eventName, { detail }))

    // Auto-update sibling <output> for range inputs
    const output = e.target.nextElementSibling
    if (output?.tagName === "OUTPUT") output.value = e.target.value
  }

  /** Toggle with visibility side-effect (e.g. shadow enabled → show/hide shadow controls) */
  toggle(e) {
    this.send(e)
    if (this.hasShadowControlsTarget) {
      this.shadowControlsTarget.classList.toggle("hidden", !e.target.checked)
    }
  }

  /** Le « + / − » d'un groupe (panneau Apparence) : il envoie, puis ouvre ou ferme le groupe
   *  tout de suite — `data-on` sur `[data-overlay-form-switch]`, que le composant lit ; le
   *  rechargement après la sauvegarde le confirme depuis le document. */
  sendSwitch(e) {
    this.send(e)
    const group = e.currentTarget.closest("[data-overlay-form-switch]")
    if (group) group.toggleAttribute("data-on", e.params.on === true)
  }

  /** Toggle button (bold/italic/…): flip the pressed visual state, then send.
   *  Active classes are listed in data-overlay-form-active-class (space-separated). */
  sendToggle(e) {
    const el = e.currentTarget
    const classes = (el.dataset.overlayFormActiveClass || "").split(/\s+/).filter(Boolean)
    classes.forEach((c) => el.classList.toggle(c))
    this.send(e)
  }

  /** Choice button in a group (justification, overflow…): mark it active among the
   *  buttons of its group (its siblings with the same event), then send.
   *  Classes come from data-overlay-form-active-class / -idle-class (space-separated). */
  sendChoice(e) {
    const el = e.currentTarget
    const event = el.dataset.overlayFormEventParam
    const split = (v) => (v || "").split(/\s+/).filter(Boolean)
    for (const btn of el.parentElement.children) {
      if (btn.dataset.overlayFormEventParam !== event) continue
      const on = btn === el
      split(btn.dataset.overlayFormActiveClass).forEach((c) => btn.classList.toggle(c, on))
      split(btn.dataset.overlayFormIdleClass).forEach((c) => btn.classList.toggle(c, !on))
    }
    this.send(e)
  }

  /** Reflète l'état de l'éditeur (le detail d'un événement window) sur les boutons du
   *  panneau : un bouton `data-overlay-form-key="italic" data-overlay-form-match="true"`
   *  est pressé (aria-pressed) quand detail.italic vaut "true". Une valeur "mixed" ne
   *  presse rien. Les boutons portent leur état en attribut, le composant le stylise. */
  reflect(e) {
    const state = e.detail || {}
    for (const el of this.element.querySelectorAll("[data-overlay-form-key]")) {
      const value = state[el.dataset.overlayFormKey]
      el.setAttribute("aria-pressed", String(value !== undefined && String(value) === el.dataset.overlayFormMatch))
    }
  }

  /** Sur mousedown d'un bouton : il ne prend pas le focus — un texte en édition sur le
   *  canvas garde le sien et sa sélection, le réglage s'y applique et la frappe continue.
   *  Posé sur un panneau entier, les champs (curseurs, couleur native) gardent leur focus. */
  keepFocus(e) {
    if (e.target.closest("button")) e.preventDefault()
  }

  // ── Private ──

  _buildDetail(e) {
    // currentTarget: a click on a child (e.g. the <span> inside a font
    // button) must still resolve to the element carrying the data-attrs
    const el = e.currentTarget || e.target
    if (el.type === "checkbox") return { enabled: el.checked }
    // Un champ caché porte une valeur composée (le color picker : une couleur CSS)
    if (el.type === "hidden") return { value: el.value }
    // Side-keyed input (container padding) — must win over the generic
    // number branch below, which would drop the side
    if (el.dataset.side) return { side: el.dataset.side, value: Number(el.value) }
    if (el.type === "range" || el.type === "number") return { value: Number(el.value) }
    // Un champ qui se glisse ou se tape (contrôleur range-field) : sa valeur posée
    if (el.dataset.rangeFieldTarget === "input") return { value: Number(el.value) }
    // Font button
    if (el.dataset.fontFamily) return { family: el.dataset.fontFamily, weight: el.dataset.fontWeight }
    // Text style toggle button
    if (el.dataset.style) return { style: el.dataset.style }
    // Layout mode button
    if (el.dataset.layoutMode) return { mode: el.dataset.layoutMode }
    // Choice button (overflow, alignment, justification…)
    if (el.dataset.value != null) return { value: el.dataset.value }
    return {}
  }
}
